import { useSyncExternalStore } from "react";
import { SCROLL_CAPABILITY_STORAGE_KEY } from "@/lib/deviceScopedStorageKeys.ts";

/**
 * Whether the user can scroll without a visible scrollbar (mouse wheel,
 * trackpad, or touch).
 *
 * We can't detect a scroll wheel's *presence* up front, so we assume the user
 * can't scroll another way until we observe a wheel/touch scroll. Until then a
 * draggable native scrollbar stays visible so mouse-only users can still scroll
 * (BIG-5785); once they scroll by wheel/trackpad/touch we hide it and fall back
 * to the custom fading thumb. Touch devices (coarse pointer) count immediately.
 * The result is persisted per browser so a returning user doesn't see the bar
 * flash before their first scroll — under a device-scoped key, so it also
 * survives logout.
 *
 * `document.body[data-native-scrollbar]` mirrors this for CSS (see calendar.css):
 * `"true"` keeps the native bar, `"false"` hides it.
 */
function readPersisted(): boolean {
  try {
    return localStorage.getItem(SCROLL_CAPABILITY_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function hasCoarsePointer(): boolean {
  try {
    return window.matchMedia("(any-pointer: coarse)").matches;
  } catch {
    return false;
  }
}

let capable = typeof window !== "undefined" && (readPersisted() || hasCoarsePointer());
const listeners = new Set<() => void>();

function applyBodyAttr(): void {
  if (typeof document !== "undefined") {
    document.body.dataset["nativeScrollbar"] = capable ? "false" : "true";
  }
}

function detach(): void {
  window.removeEventListener("wheel", markCapable);
  window.removeEventListener("touchmove", markCapable);
}

function markCapable(): void {
  if (capable) return;
  capable = true;
  try {
    localStorage.setItem(SCROLL_CAPABILITY_STORAGE_KEY, "true");
  } catch {
    // Private mode / storage disabled: still hide the bar for this session.
  }
  applyBodyAttr();
  detach();
  for (const notify of listeners) notify();
}

if (typeof window !== "undefined") {
  applyBodyAttr();
  if (!capable) {
    window.addEventListener("wheel", markCapable, { passive: true });
    window.addEventListener("touchmove", markCapable, { passive: true });
  }
}

function subscribe(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
}

function getSnapshot(): boolean {
  return capable;
}

/** True once the user has scrolled via wheel/trackpad/touch (or is on a touch
 *  device). While false, keep a visible native scrollbar for mouse-only users. */
export function useCanScrollWithoutBar(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot);
}
